import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Agent, agentApi } from '../services/api';

interface AgentContextType {
  selectedAgent: Agent | null;
  setSelectedAgent: (agent: Agent | null) => void;
  loadingAgent: boolean;
  agentError: string | null;
  fetchAgentById: (id: string) => Promise<Agent | null>;
}

// Create context with default values
const AgentContext = createContext<AgentContextType>({
  selectedAgent: null,
  setSelectedAgent: () => {},
  loadingAgent: false,
  agentError: null,
  fetchAgentById: async () => null,
});

export const useAgentContext = () => useContext(AgentContext);

export const AgentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [loadingAgent, setLoadingAgent] = useState<boolean>(false);
  const [agentError, setAgentError] = useState<string | null>(null);

  // Function to fetch an agent by ID
  const fetchAgentById = async (id: string): Promise<Agent | null> => {
    if (!id) return null;

    setLoadingAgent(true);
    setAgentError(null);

    try {
      const agent = await agentApi.getAgent(id);
      setSelectedAgent(agent);
      return agent;
    } catch (error) {
      console.error('Error fetching agent:', error);
      setAgentError('Failed to load the selected agent');
      setSelectedAgent(null);
      return null;
    } finally {
      setLoadingAgent(false);
    }
  };

  const value = {
    selectedAgent,
    setSelectedAgent,
    loadingAgent,
    agentError,
    fetchAgentById,
  };

  return (
    <AgentContext.Provider value={value}>{children}</AgentContext.Provider>
  );
};

export default AgentProvider;
